<template>
  <div class="hp-builder" dir="rtl">
    <CCard>
      <CCardHeader class="d-flex align-items-center flex-wrap">
        <strong class="ml-3">تنظیمات صفحه اصلی</strong>
        <span v-if="dirty" class="badge badge-warning ml-2">تغییرات ذخیره نشده</span>
        <span v-else-if="config.updatedAt" class="small text-muted ml-2">آخرین ذخیره: {{ formatDate(config.updatedAt) }}</span>
        <div class="mr-auto d-flex align-items-center flex-wrap">
          <CSelect :options="typeOptions" :value.sync="addType" size="sm" class="mb-0 ml-1" />
          <CButton color="success" size="sm" class="ml-2" @click="addSection">+ افزودن بخش</CButton>
          <CButton color="secondary" variant="outline" size="sm" class="ml-2" @click="resetDefault">بارگذاری پیش‌فرض</CButton>
          <CButton color="primary" size="sm" :disabled="saving || !dirty" @click="save">{{ saving ? 'در حال ذخیره...' : 'ذخیره تغییرات' }}</CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <p class="text-muted small mb-3">
          بخش‌ها را با کشیدن و رها کردن (یا دکمه‌های ▲▼) مرتب کنید. هر بخش را می‌توان فعال/غیرفعال کرد و برای دسکتاپ، موبایل یا هر دو نمایش داد.
        </p>
        <div v-if="loading" class="text-center text-muted py-5">در حال بارگذاری...</div>
        <div v-else-if="!config.sections.length" class="text-center text-muted py-5">هیچ بخشی وجود ندارد. از بالا یک بخش اضافه کنید.</div>

        <div
          v-for="(sec, si) in config.sections"
          :key="sec.id"
          class="hp-section card mb-2"
          :class="{ 'hp-disabled': !sec.enabled, 'hp-drop': dropIndex === si }"
          @dragover.prevent="dropIndex = si"
          @dragleave="dropIndex = dropIndex === si ? -1 : dropIndex"
          @drop.prevent="onDrop(si)"
        >
          <div class="card-header d-flex align-items-center flex-wrap py-2">
            <span class="hp-handle ml-2" draggable="true" title="جابجایی" @dragstart="onDragStart(si, $event)" @dragend="dragIndex = -1; dropIndex = -1">☰</span>
            <span class="badge badge-info ml-2">{{ typeLabel(sec.type) }}</span>
            <strong class="ml-2 hp-title" @click="toggleOpen(sec.id)">{{ sec.title || 'بدون عنوان' }}</strong>
            <div class="mr-auto d-flex align-items-center flex-wrap">
              <CSelect :options="deviceOptions" :value.sync="sec.device" size="sm" class="mb-0 ml-2 hp-device" />
              <CSwitch color="success" shape="pill" :checked.sync="sec.enabled" class="ml-2" title="فعال/غیرفعال" />
              <CButton size="sm" variant="ghost" color="secondary" :disabled="si === 0" @click="moveSection(si, -1)">▲</CButton>
              <CButton size="sm" variant="ghost" color="secondary" :disabled="si === config.sections.length - 1" @click="moveSection(si, 1)">▼</CButton>
              <CButton size="sm" variant="ghost" color="info" @click="duplicate(si)">کپی</CButton>
              <CButton size="sm" variant="ghost" color="danger" @click="askDelete(si)">حذف</CButton>
              <CButton size="sm" variant="ghost" color="dark" @click="toggleOpen(sec.id)">{{ open[sec.id] ? 'بستن' : 'ویرایش' }}</CButton>
            </div>
          </div>

          <div v-if="open[sec.id]" class="card-body">
            <CRow>
              <CCol md="6"><CInput v-model="sec.title" label="عنوان بخش" /></CCol>
              <CCol v-if="sec.type === 'banner_grid'" md="3">
                <CSelect label="تعداد ستون" :options="[{ value: 1, label: '۱ ستون' }, { value: 2, label: '۲ ستون' }, { value: 4, label: '۴ ستون' }]" :value.sync="sec.settings.columns" />
              </CCol>
              <CCol v-if="sec.type === 'hero_slider'" md="3">
                <CInput v-model.number="sec.settings.interval" type="number" label="فاصله اسلایدها (میلی‌ثانیه)" />
              </CCol>
            </CRow>

            <!-- product based sections -->
            <template v-if="sec.type === 'product_carousel' || sec.type === 'amazing_offers'">
              <CRow>
                <CCol md="3">
                  <CSelect label="منبع محصولات" :options="sec.type === 'amazing_offers' ? offerSources : sourceOptions" :value.sync="sec.settings.source" />
                </CCol>
                <CCol md="2"><CInput v-model.number="sec.settings.limit" type="number" min="1" max="40" label="تعداد" /></CCol>
                <CCol md="4"><CInput v-model="sec.settings.viewAllLink" label="لینک «مشاهده همه»" placeholder="/shop/cat/..." /></CCol>
                <CCol v-if="sec.type === 'product_carousel' && sec.settings.source === 'category'" md="3">
                  <CSelect label="دسته‌بندی" :options="categoryOptions" :value.sync="sec.settings.categoryId" />
                </CCol>
                <CCol v-if="sec.type === 'product_carousel' && sec.settings.source === 'top_category'" md="3">
                  <CInput v-model.number="sec.settings.categoryIndex" type="number" min="1" max="30" label="شماره دسته‌بندی اصلی" description="عنوان و «مشاهده همه» در صورت خالی بودن از همین دسته پر می‌شود" />
                </CCol>
              </CRow>
              <CRow v-if="sec.type === 'amazing_offers'">
                <CCol md="4">
                  <label class="d-block">پایان شمارش معکوس</label>
                  <date-picker
                    :value="toPicker(sec.settings.endsAt)"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    display-format="jYYYY/jMM/jDD HH:mm"
                    :clearable="true"
                    @input="sec.settings.endsAt = fromPicker($event)"
                  />
                </CCol>
                <CCol md="3">
                  <label class="d-block">رنگ پس‌زمینه</label>
                  <div class="d-flex align-items-center">
                    <input v-model="sec.settings.bgColor" type="color" class="ml-2">
                    <CInput v-model="sec.settings.bgColor" size="sm" class="mb-0" placeholder="#ef394e" />
                  </div>
                </CCol>
              </CRow>
              <HpProductPicker v-if="sec.settings.source === 'manual'" v-model="sec.settings.productIds" class="mt-2" />
            </template>

            <!-- html -->
            <template v-if="sec.type === 'html_block'">
              <CTextarea v-model="sec.settings.html" label="کد HTML" rows="8" dir="ltr" />
              <small class="text-muted">اسکریپت و iframe برای امنیت حذف می‌شوند.</small>
            </template>

            <!-- automatic sources -->
            <CRow v-if="sec.type === 'category_icons' || sec.type === 'brand_strip'" class="mt-2">
              <CCol md="6">
                <CInputCheckbox :checked.sync="sec.settings.auto" :label="sec.type === 'category_icons' ? 'اگر آیتمی تعریف نشده، دسته‌بندی‌های اصلی سایت خودکار نمایش داده شوند' : 'اگر آیتمی تعریف نشده، برندهای دارای لوگو خودکار نمایش داده شوند'" custom />
              </CCol>
              <CCol md="2"><CInput v-model.number="sec.settings.limit" type="number" min="1" max="40" label="حداکثر تعداد" size="sm" /></CCol>
            </CRow>
            <CRow v-if="sec.type === 'shipping_info'" class="mt-2">
              <CCol md="4"><CInput v-model="sec.settings.bgColor" label="رنگ پس‌زمینه (اختیاری)" placeholder="#f5f5f5" size="sm" /></CCol>
            </CRow>

            <!-- item based sections -->
            <template v-if="hasItems(sec.type)">
              <hr>
              <div class="d-flex align-items-center mb-2">
                <strong>آیتم‌ها ({{ sec.items.length }})</strong>
                <CButton size="sm" color="success" variant="outline" class="mr-auto" @click="addItem(sec)">+ افزودن آیتم</CButton>
              </div>
              <div v-for="(it, ii) in sec.items" :key="sec.id + '_' + ii" class="hp-item border rounded p-2 mb-2">
                <div class="d-flex align-items-center mb-2">
                  <span class="small text-muted">آیتم {{ ii + 1 }}</span>
                  <div class="mr-auto">
                    <CButton size="sm" variant="ghost" color="secondary" :disabled="ii === 0" @click="moveItem(sec, ii, -1)">▲</CButton>
                    <CButton size="sm" variant="ghost" color="secondary" :disabled="ii === sec.items.length - 1" @click="moveItem(sec, ii, 1)">▼</CButton>
                    <CButton size="sm" variant="ghost" color="danger" @click="removeItem(sec, ii)">✕</CButton>
                  </div>
                </div>
                <CRow v-if="sec.type === 'hero_slider' || sec.type === 'banner_grid'">
                  <CCol md="6"><HpImageUpload v-model="it.image" label="تصویر دسکتاپ" /></CCol>
                  <CCol md="6"><HpImageUpload v-model="it.mobileImage" label="تصویر موبایل (اختیاری)" /></CCol>
                  <CCol md="6"><CInput v-model="it.link" label="لینک" size="sm" /></CCol>
                  <CCol md="6"><CInput v-model="it.alt" label="متن جایگزین (alt)" size="sm" /></CCol>
                </CRow>
                <CRow v-else-if="sec.type === 'category_icons'">
                  <CCol md="4"><CSelect label="دسته‌بندی" :options="categoryOptions" :value.sync="it.categoryId" size="sm" /></CCol>
                  <CCol md="4"><CInput v-model="it.title" label="عنوان (خالی = نام دسته)" size="sm" /></CCol>
                  <CCol md="4"><CInput v-model="it.link" label="لینک (خالی = صفحه دسته)" size="sm" /></CCol>
                  <CCol md="6"><HpImageUpload v-model="it.icon" label="آیکون" /></CCol>
                </CRow>
                <CRow v-else-if="sec.type === 'brand_strip'">
                  <CCol md="6"><HpImageUpload v-model="it.logo" label="لوگو" /></CCol>
                  <CCol md="3"><CInput v-model="it.title" label="نام برند" size="sm" /></CCol>
                  <CCol md="3"><CInput v-model="it.link" label="لینک" size="sm" /></CCol>
                </CRow>
                <CRow v-else-if="sec.type === 'story_circles'">
                  <CCol md="6"><HpImageUpload v-model="it.image" label="تصویر" /></CCol>
                  <CCol md="3"><CInput v-model="it.title" label="عنوان" size="sm" /></CCol>
                  <CCol md="3"><CInput v-model="it.link" label="لینک" size="sm" /></CCol>
                </CRow>
                <CRow v-else-if="sec.type === 'shipping_info'">
                  <CCol md="3"><CSelect label="آیکن" :options="shippingIcons" :value.sync="it.icon" size="sm" /></CCol>
                  <CCol md="3"><CInput v-model="it.title" label="عنوان" size="sm" /></CCol>
                  <CCol md="3"><CInput v-model="it.subtitle" label="توضیح کوتاه" size="sm" /></CCol>
                  <CCol md="3"><CInput v-model="it.link" label="لینک (اختیاری)" size="sm" /></CCol>
                  <CCol md="6"><HpImageUpload v-model="it.image" label="آیکن سفارشی (اختیاری)" /></CCol>
                </CRow>
              </div>
            </template>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CModal title="حذف بخش" color="danger" :show.sync="deleteModal">
      آیا از حذف بخش «{{ deleteTarget >= 0 && config.sections[deleteTarget] ? config.sections[deleteTarget].title : '' }}» مطمئن هستید؟
      <template #footer>
        <CButton color="secondary" @click="deleteModal = false">انصراف</CButton>
        <CButton color="danger" @click="confirmDelete">حذف</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import HpImageUpload from './homepage/HpImageUpload'
import HpProductPicker from './homepage/HpProductPicker'
import {
  getHomepage, getDefaultHomepage, saveHomepage, getCategoryTree,
  SECTION_TYPES, DEVICES, PRODUCT_SOURCES, SHIPPING_ICONS, typeLabel, newSection, newItem
} from './homepage/hpApi'

const ITEM_TYPES = ['hero_slider', 'banner_grid', 'category_icons', 'brand_strip', 'story_circles', 'shipping_info']
const pad = (n) => (n < 10 ? '0' : '') + n

export default {
  name: 'HomepageBuilder',
  components: { HpImageUpload, HpProductPicker },
  data() {
    return {
      loading: true,
      saving: false,
      config: { sections: [], updatedAt: null },
      snapshot: '',
      open: {},
      addType: 'product_carousel',
      typeOptions: SECTION_TYPES,
      deviceOptions: DEVICES,
      sourceOptions: PRODUCT_SOURCES,
      shippingIcons: SHIPPING_ICONS,
      offerSources: PRODUCT_SOURCES.filter((s) => s.value === 'discounted' || s.value === 'manual'),
      categoryOptions: [{ value: null, label: '— انتخاب دسته —' }],
      dragIndex: -1,
      dropIndex: -1,
      deleteModal: false,
      deleteTarget: -1
    }
  },
  computed: {
    dirty() {
      return !this.loading && JSON.stringify(this.config.sections) !== this.snapshot
    }
  },
  created() {
    this.load()
    this.loadCategories()
  },
  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty && !window.confirm('تغییرات ذخیره نشده‌اند. از این صفحه خارج می‌شوید؟')) return next(false)
    return next()
  },
  methods: {
    typeLabel,
    hasItems(t) {
      return ITEM_TYPES.indexOf(t) !== -1
    },
    notify(message, type) {
      if (this.$toast) this.$toast.open({ message: message, type: type || 'success' })
      else alert(message)
    },
    onBeforeUnload(e) {
      if (!this.dirty) return undefined
      e.preventDefault()
      e.returnValue = ''
      return ''
    },
    normalize(cfg) {
      const sections = ((cfg && cfg.sections) || []).map((s) => {
        const base = newSection(s.type)
        return Object.assign(base, s, { settings: Object.assign({}, base.settings, s.settings || {}), items: (s.items || []).map((i) => Object.assign(newItem(s.type), i)) })
      })
      return { sections: sections, updatedAt: (cfg && cfg.updatedAt) || null }
    },
    setConfig(cfg, markClean) {
      this.config = this.normalize(cfg)
      if (markClean) this.snapshot = JSON.stringify(this.config.sections)
    },
    load() {
      this.loading = true
      getHomepage().then((res) => {
        if (res.data && res.data.error === 0) this.setConfig(res.data.config, true)
        else this.notify((res.data && res.data.msg) || 'خطا در دریافت تنظیمات', 'error')
      }).catch(() => this.notify('خطا در ارتباط با سرور', 'error')).finally(() => { this.loading = false })
    },
    loadCategories() {
      getCategoryTree().then((res) => {
        const out = [{ value: null, label: '— انتخاب دسته —' }]
        const walk = (nodes, depth) => (nodes || []).forEach((n) => {
          out.push({ value: n.id, label: '— '.repeat(depth) + (n.name || n.title || n.label || ('#' + n.id)) })
          walk(n.children, depth + 1)
        })
        walk((res.data && (res.data.data || res.data.orders)) || [], 0)
        this.categoryOptions = out
      }).catch(() => {})
    },
    resetDefault() {
      if (!window.confirm('تنظیمات پیش‌فرض جایگزین بخش‌های فعلی شود؟ (تا قبل از ذخیره قابل برگشت است)')) return
      getDefaultHomepage().then((res) => {
        if (res.data && res.data.config) this.config.sections = this.normalize(res.data.config).sections
      })
    },
    save() {
      this.saving = true
      const payload = { sections: this.config.sections.map((s, i) => Object.assign({}, s, { order: i })) }
      saveHomepage(payload).then((res) => {
        if (res.data && res.data.error === 0) {
          this.setConfig(res.data.config, true)
          this.notify(res.data.msg || 'ذخیره شد')
        } else this.notify((res.data && res.data.msg) || 'خطا در ذخیره', 'error')
      }).catch((err) => {
        this.notify((err.response && err.response.data && err.response.data.msg) || 'خطا در ذخیره', 'error')
      }).finally(() => { this.saving = false })
    },
    toggleOpen(id) {
      this.$set(this.open, id, !this.open[id])
    },
    addSection() {
      const s = newSection(this.addType)
      this.config.sections.push(s)
      this.$set(this.open, s.id, true)
    },
    duplicate(i) {
      const copy = JSON.parse(JSON.stringify(this.config.sections[i]))
      copy.id = 's_' + Math.random().toString(16).slice(2, 12)
      copy.title = (copy.title || '') + ' (کپی)'
      this.config.sections.splice(i + 1, 0, copy)
    },
    askDelete(i) {
      this.deleteTarget = i
      this.deleteModal = true
    },
    confirmDelete() {
      if (this.deleteTarget >= 0) this.config.sections.splice(this.deleteTarget, 1)
      this.deleteTarget = -1
      this.deleteModal = false
    },
    moveSection(i, d) {
      const l = this.config.sections
      const t = l.splice(i, 1)[0]
      l.splice(i + d, 0, t)
    },
    onDragStart(i, e) {
      this.dragIndex = i
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(i))
      }
    },
    onDrop(i) {
      const from = this.dragIndex
      this.dropIndex = -1
      this.dragIndex = -1
      if (from < 0 || from === i) return
      const l = this.config.sections
      const t = l.splice(from, 1)[0]
      l.splice(i, 0, t)
    },
    addItem(sec) {
      sec.items.push(newItem(sec.type))
    },
    removeItem(sec, i) {
      sec.items.splice(i, 1)
    },
    moveItem(sec, i, d) {
      const t = sec.items.splice(i, 1)[0]
      sec.items.splice(i + d, 0, t)
    },
    toPicker(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    fromPicker(v) {
      if (!v) return ''
      const d = new Date(String(v).replace(' ', 'T'))
      return isNaN(d.getTime()) ? '' : d.toISOString()
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString('fa-IR')
      } catch (e) {
        return iso
      }
    }
  }
}
</script>

<style scoped>
.hp-section { transition: box-shadow .15s; }
.hp-section.hp-disabled { opacity: .6; }
.hp-section.hp-drop { box-shadow: 0 0 0 2px #321fdb; }
.hp-handle { cursor: grab; font-size: 18px; user-select: none; }
.hp-title { cursor: pointer; }
.hp-device { min-width: 140px; }
.hp-item { background: #fafbfc; }
</style>
